import type { TextNoteDownloadParams } from "@/object-actions/attachTextNoteDownloadVariants";
import { buildCanvasNodeKind } from "@/object-actions/handlers/canvasNode";
import { ObjectActionCommandMenu } from "@/object-actions/renderers/ObjectActionCommandMenu";
import type { ObjectActionHandlers } from "@/object-actions/types";
import { useCanvasEntityObjectActions } from "@/object-actions/useCanvasEntityObjectActions";
import { useCanvasEntityNavigationHandlers } from "@/pages/canvas/hooks/useCanvasEntityNavigationHandlers";
import { useCanvasFileRecordMenuHandlerDeps } from "@/pages/canvas/hooks/useCanvasFileRecordMenuHandlerDeps";
import type { FileRecord } from "@cohesive/api-client";
import type { Entity } from "@cohesive/ui-components";
import { useMemo } from "react";

export type NodeContextMenuCursorPosition = {
  screenX: number;
  screenY: number;
};

type CanvasNodeContextMenuProps = {
  canvasId: string | undefined;
  cursorPosition: NodeContextMenuCursorPosition;
  nodeType: string | undefined;
  entity?: Entity | null;
  entityBase?: string;
  fileRecord?: FileRecord | null;
  onClose: () => void;
  handlers: ObjectActionHandlers;
  textNoteDownload?: TextNoteDownloadParams | null;
  state?: {
    copyDisabled?: boolean;
    zOrderDisabled?: boolean;
    canEdit?: boolean;
    /** The node is an HTML block — a document's actions, plus Expand. */
    isCanvasIntrinsic?: boolean;
  };
};

type CanvasNodeKind = NonNullable<ReturnType<typeof buildCanvasNodeKind>>;

export function CanvasNodeContextMenu(props: CanvasNodeContextMenuProps) {
  // Resolve the kind (a plain function, not a hook) and bail before mounting the
  // inner component so the inner hook set is always the same across renders —
  // early-returning inside the hook-calling component would break Rules of Hooks
  // when the same instance later targets a different node type.
  const kind = buildCanvasNodeKind(
    props.nodeType,
    props.entityBase,
    props.fileRecord?.kind === "link",
  );
  if (!kind) {
    return null;
  }
  return <CanvasNodeContextMenuInner {...props} kind={kind} />;
}

function CanvasNodeContextMenuInner({
  canvasId,
  cursorPosition,
  nodeType,
  entity,
  fileRecord,
  onClose,
  handlers,
  textNoteDownload,
  state,
  kind,
}: CanvasNodeContextMenuProps & { kind: CanvasNodeKind }) {
  const canEdit = state?.canEdit ?? true;

  const {
    handlers: entityNavigationHandlers,
    hasWebLink,
    hasDetailView,
  } = useCanvasEntityNavigationHandlers(
    nodeType === "entity" ? entity : null,
    canEdit,
    () => {},
  );

  const nodeHandlers = useMemo(
    () => ({
      // Entity-nav supplies open / open-in-new-tab / copy-url; the node-level
      // handlers (copy, delete = remove-from-canvas, z-order) must win — the
      // nav hook only has a no-op delete here.
      ...entityNavigationHandlers,
      ...handlers,
    }),
    [entityNavigationHandlers, handlers],
  );

  const fileRecordHandlerDeps = useCanvasFileRecordMenuHandlerDeps(
    canvasId,
    fileRecord ?? null,
  );

  const { actions } = useCanvasEntityObjectActions({
    kind,
    surface: "context-menu",
    nodeHandlers,
    fileRecord,
    fileRecordHandlerDeps: fileRecordHandlerDeps ?? {},
    textNoteDownload,
    state: {
      copyDisabled:
        state?.copyDisabled ??
        (nodeType === "chatSession" || nodeType === "commentMarker"),
      zOrderDisabled:
        state?.zOrderDisabled ??
        (nodeType === "section" ||
          nodeType === "chatSession" ||
          nodeType === "commentMarker"),
      canEdit,
      hasWebLink,
      hasDetailView,
      isCanvasIntrinsic: state?.isCanvasIntrinsic ?? false,
    },
  });

  return (
    <ObjectActionCommandMenu
      cursorPosition={cursorPosition}
      actions={actions}
      kind={kind}
      onClose={onClose}
    />
  );
}
